// Fixed version - frontend/src/components/Debug/NotificationDebug.js

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const NotificationDebug = () => {
  const [logs, setLogs] = useState([]);
  const [swStatus, setSWStatus] = useState('checking...');
  const [notificationPermission, setNotificationPermission] = useState('checking...');
  const { user } = useAuth();

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { message, type, timestamp }]);
  };

  // Check status on component mount
  useEffect(() => {
    checkServiceWorkerStatus();
    checkNotificationPermission();
  }, []);

  const checkServiceWorkerStatus = async () => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const controller = navigator.serviceWorker.controller;
        
        setSWStatus(controller ? 'active' : 'no controller');
        addLog(`Service Worker Status: ${controller ? 'Active and Ready' : 'No Controller'}`, 'info');
        addLog(`SW Registration State: ${registration.active?.state}`, 'info');
      } catch (error) {
        setSWStatus('error');
        addLog(`Service Worker Error: ${error.message}`, 'error');
      }
    } else {
      setSWStatus('not supported');
      addLog('Service Workers not supported in this browser', 'error');
    }
  };

  const checkNotificationPermission = () => {
    if ('Notification' in window) {
      const permission = Notification.permission;
      setNotificationPermission(permission);
      addLog(`Notification Permission: ${permission}`, permission === 'granted' ? 'success' : 'warning');
    } else {
      setNotificationPermission('not supported');
      addLog('Notifications not supported in this browser', 'error');
    }
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      addLog(`Permission ${permission === 'granted' ? 'granted' : 'denied'}`, permission === 'granted' ? 'success' : 'error');
    }
  };

  const testDirectNotification = () => {
    addLog('Testing direct notification...', 'info');
    
    if (Notification.permission !== 'granted') {
      addLog('Permission not granted for direct notification', 'error');
      return;
    }

    try {
      const notification = new Notification('🧪 Direct Test Notification', {
        body: 'This is a direct notification test (app must be active)',
        icon: '/favicon.ico',
        tag: 'direct-test',
        requireInteraction: false
      });

      notification.onclick = () => {
        addLog('Direct notification clicked', 'success');
        notification.close();
      };

      notification.onshow = () => {
        addLog('Direct notification shown successfully', 'success');
      };

      notification.onerror = (error) => {
        addLog(`Direct notification error: ${error}`, 'error');
      };

      setTimeout(() => notification.close(), 5000);
      
    } catch (error) {
      addLog(`Direct notification failed: ${error.message}`, 'error');
    }
  };

  const testServiceWorkerMessage = async () => {
    addLog('Testing service worker message...', 'info');
    
    if (!navigator.serviceWorker.controller) {
      addLog('No service worker controller available', 'error');
      return;
    }

    try {
      // Test ping first
      navigator.serviceWorker.controller.postMessage({
        type: 'ping',
        timestamp: Date.now()
      });
      
      addLog('Ping message sent to service worker', 'info');
      
      // Test notification message
      setTimeout(() => {
        navigator.serviceWorker.controller.postMessage({
          type: 'show_notification',
          title: '🧪 Service Worker Test',
          message: 'This notification should work in background!',
          distance_miles: 1.5,
          estimated_arrival: 3,
          bus_id: 'test-bus',
          notification_id: 'sw-test-' + Date.now(),
          urgency: 'medium'
        });
        
        addLog('Notification message sent to service worker', 'info');
        addLog('🔥 CHECK YOUR NOTIFICATIONS NOW! (even if app goes to background)', 'success');
      }, 1000);
      
    } catch (error) {
      addLog(`Service worker message failed: ${error.message}`, 'error');
    }
  };

  const checkWebSocketStatus = () => {
    addLog('Checking WebSocket status...', 'info');
    
    // Check if user is available
    addLog(`Current user: ${user?.email || 'Not logged in'}`, user?.email ? 'success' : 'error');
    
    // Note: We can't directly access wsRef from useVehicleTracker here
    // But we can check if WebSocket is generally available
    if ('WebSocket' in window) {
      addLog('WebSocket API supported', 'success');
      
      // Try to find WebSocket connections (this is a general check)
      addLog('WebSocket connections cannot be directly inspected from this component', 'warning');
      addLog('Check browser DevTools → Network → WS tab for active WebSocket connections', 'info');
    } else {
      addLog('WebSocket API not supported', 'error');
    }
  };

  const testWebSocketHandler = () => {
    addLog('Testing WebSocket message handling...', 'info');
    
    // Simulate receiving a bus notification through WebSocket
    const testWebSocketData = {
      type: 'bus_notification',
      notification_id: 'websocket-test-' + Date.now(),
      message: 'WebSocket test: Bus approaching!',
      distance_miles: 1.5,
      estimated_arrival: 3,
      bus_id: 'websocket-test-bus'
    };
    addLog('WebSocket test data logged to console', 'info');
    
    // Note: We can't directly call handleBusNotification from here
    // But we can test the service worker path directly
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'show_notification',
        title: '🧪 WebSocket Handler Test',
        message: testWebSocketData.message,
        distance_miles: testWebSocketData.distance_miles,
        estimated_arrival: testWebSocketData.estimated_arrival,
        bus_id: testWebSocketData.bus_id,
        notification_id: testWebSocketData.notification_id,
        urgency: 'medium'
      });
      
      addLog('WebSocket handler test sent to service worker', 'success');
      addLog('🔥 Should trigger background notification!', 'success');
    } else {
      addLog('No service worker controller for WebSocket test', 'error');
    }
  };

  const clearLogs = () => {
    setLogs([]);
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'success': return 'text-green-600';
      case 'error': return 'text-red-600';
      case 'warning': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6">🔧 Notification System Debug</h2>
      
      {/* Status Panel */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="p-4 bg-gray-50 rounded">
          <h3 className="font-semibold">Service Worker</h3>
          <p className={`text-sm ${swStatus === 'active' ? 'text-green-600' : 'text-red-600'}`}>
            Status: {swStatus}
          </p>
        </div>
        <div className="p-4 bg-gray-50 rounded">
          <h3 className="font-semibold">Notifications</h3>
          <p className={`text-sm ${notificationPermission === 'granted' ? 'text-green-600' : 'text-red-600'}`}>
            Permission: {notificationPermission}
          </p>
        </div>
      </div>

      {/* WebSocket Status Panel */}
      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded mb-6">
        <h4 className="font-bold text-blue-800 mb-2">🔌 WebSocket & User Status</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <button 
            onClick={checkWebSocketStatus}
            className="bg-blue-500 text-white px-4 py-2 rounded mr-2"
          >
            Check WebSocket Support
          </button>
          
          <button 
            onClick={testWebSocketHandler}
            className="bg-green-500 text-white px-4 py-2 rounded"
          >
            Test WebSocket Handler
          </button>
        </div>
        
        <div className="text-sm text-blue-700">
          <p>• Current user: <strong>{user?.email || 'Not logged in'}</strong></p>
          <p>• User ID: <strong>{user?.id || 'No ID'}</strong></p>
          <p>• Check DevTools → Network → WS for active WebSocket connections</p>
        </div>
      </div>

      {/* Test Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <button
          onClick={checkServiceWorkerStatus}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Check SW Status
        </button>
        
        <button
          onClick={requestNotificationPermission}
          className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          disabled={notificationPermission === 'granted'}
        >
          Request Permission
        </button>
        
        <button
          onClick={testDirectNotification}
          className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
        >
          Test Direct
        </button>
        
        <button
          onClick={testServiceWorkerMessage}
          className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600"
        >
          Test SW Message
        </button>
      </div>

      {/* Instructions */}
      <div className="mb-6 p-4 bg-blue-50 rounded border border-blue-200">
        <h3 className="font-semibold text-blue-800 mb-2">Test Instructions:</h3>
        <ol className="text-sm text-blue-700 space-y-1">
          <li>1. Click "Request Permission" if needed</li>
          <li>2. Click "Test Direct" to verify basic notifications work</li>
          <li>3. Click "Test SW Message" to test background notifications</li>
          <li>4. <strong>Immediately minimize/background the app</strong> after step 3</li>
          <li>5. System notification should appear even with app closed</li>
          <li>6. Click "Test WebSocket Handler" to simulate real bus notifications</li>
        </ol>
      </div>

      {/* Debug Console */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-semibold">Debug Console</h3>
          <button
            onClick={clearLogs}
            className="text-sm bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600"
          >
            Clear
          </button>
        </div>
        <div className="bg-gray-900 text-gray-100 p-4 rounded max-h-64 overflow-y-auto font-mono text-sm">
          {logs.length === 0 ? (
            <p className="text-gray-400">No logs yet. Run some tests!</p>
          ) : (
            logs.map((log, index) => (
              <div key={index} className={`${getLogColor(log.type)} mb-1`}>
                <span className="text-gray-400">[{log.timestamp}]</span> {log.message}
              </div>
            ))
          )}
        </div>
      </div>

      {/* DevTools Instructions */}
      <div className="p-4 bg-gray-50 rounded border">
        <h3 className="font-semibold mb-2">DevTools Debugging:</h3>
        <ul className="text-sm space-y-1">
          <li>• Open DevTools → Console to see detailed logs</li>
          <li>• Check DevTools → Application → Service Workers</li>
          <li>• Check DevTools → Network → WS tab for WebSocket connections</li>
          <li>• Look for messages starting with 🔧, 📨, 🔔</li>
        </ul>
      </div>
    </div>
  );
};

export default NotificationDebug;